import * as XLSX from 'xlsx';
import type { WarehouseGoodsItem } from '../types/warehouseGoodsCatalog';

const norm = (s: string) =>
  String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

/** Chuẩn hóa tên để so sánh trùng (bỏ dấu, thường). */
export const normalizeGoodsNameKey = (s: string) => norm(s).replace(/\s+/g, ' ').trim();

export const parseGramValue = (raw: string | number | undefined): number | undefined => {
  if (raw === undefined || raw === null) return undefined;
  if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return raw;
  const t = String(raw)
    .trim()
    .replace(/\s/g, '')
    .replace(/,/g, '.');
  const n = Number(t);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return n;
};

/** Gợi ý quy đổi khi file không có cột gram. */
export const inferDefaultGramsPerBaseUnit = (baseUnit: string): number => {
  const k = norm(baseUnit).replace(/\s+/g, '').replace(/\./g, '');
  if (k === 'g' || k === 'gam' || k === 'gram') return 1;
  if (k === 'kg' || k === 'kilo' || k === 'kilogram') return 1000;
  return 1000;
};

/** Nhận diện cột Excel / object key */
const pick = (row: Record<string, unknown>, keys: string[]): string => {
  const map = new Map<string, string>();
  Object.keys(row).forEach((k) => {
    map.set(norm(k), String((row as any)[k] ?? '').trim());
  });
  for (const k of keys) {
    const v = map.get(norm(k));
    if (v !== undefined && v !== '') return v;
  }
  return '';
};

const pickGram = (row: Record<string, unknown>): number | undefined => {
  const map = new Map<string, string>();
  Object.keys(row).forEach((k) => {
    map.set(norm(k), String((row as any)[k] ?? '').trim());
  });
  const keys = [
    'Quy đổi G',
    'Quy doi G',
    'Quy đổi g',
    'Gram / đơn vị',
    'g / đơn vị',
    'gramsPerBaseUnit',
    'gramConversion',
    'quyDoiG',
  ];
  for (const k of keys) {
    const v = map.get(norm(k));
    if (v !== undefined && v !== '') {
      const n = parseGramValue(v);
      if (n !== undefined) return n;
    }
  }
  return undefined;
};

export const isMassBaseUnit = (baseUnit: string): boolean => {
  const k = norm(baseUnit).replace(/\s+/g, '').replace(/\./g, '');
  return ['g', 'gam', 'gram', 'kg', 'kilo', 'kilogram'].includes(k);
};

export type ParsedGoodsRow = {
  productCode: string;
  name: string;
  group: string;
  baseUnit: string;
  gramsPerBaseUnit?: number;
  lineNumber: number;
};

const splitLine = (line: string): string[] => {
  if (line.includes('\t')) return line.split('\t').map((c) => c.trim());
  if (line.includes(';')) return line.split(';').map((c) => c.trim());
  return line.split(/\s{2,}|,\s*/).map((c) => c.trim());
};

function autoProductCodeFromName(name: string, used: Set<string>): string {
  let base = norm(name)
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  if (!base) base = 'hang';
  if (!/^[a-z_]/.test(base)) base = `dm_${base}`;
  let code = base;
  let i = 2;
  while (used.has(code.toLowerCase())) {
    code = `${base}_${i}`;
    i += 1;
  }
  used.add(code.toLowerCase());
  return code.toUpperCase();
}

const isHeaderLine = (parts: string[], lineNumber: number): boolean => {
  if (lineNumber !== 1) return false;
  const j = norm(parts.join(' '));
  return (
    (j.includes('ten') && (j.includes('don vi') || j.includes('dvt'))) ||
    j.includes('quy doi') ||
    j.includes('ma san pham') ||
    j.includes('ten nguyen lieu')
  );
};

/**
 * Dán từ Excel/Google Sheet:
 * - 5 cột: Mã, Tên, Nhóm, Đơn vị, Quy đổi G
 * - 4 cột: Mã, Tên, Nhóm, Đơn vị (quy đổi suy từ đơn vị) HOẶC Mã, Tên, Đơn vị, Quy đổi G (cột 4 là số)
 * - 3 cột: Tên, Đơn vị, Quy đổi G (mã tự sinh)
 */
export const parseGoodsCatalogPaste = (raw: string): ParsedGoodsRow[] => {
  const lines = raw
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  const out: ParsedGoodsRow[] = [];
  let lineNumber = 0;
  const usedCodes = new Set<string>();

  for (const line of lines) {
    lineNumber += 1;
    const parts = splitLine(line).filter((p) => p.length > 0);
    if (parts.length < 2) continue;
    if (isHeaderLine(parts, lineNumber)) continue;

    let productCode = '';
    let name = '';
    let group = '';
    let baseUnit = '';
    let grams: number | undefined;

    if (parts.length >= 5) {
      productCode = parts[0] || '';
      name = parts[1] || '';
      group = parts[2] || '';
      baseUnit = parts[3] || '';
      grams = parseGramValue(parts[4]);
    } else if (parts.length === 4) {
      const g4 = parseGramValue(parts[3]);
      if (g4 !== undefined) {
        productCode = parts[0] || '';
        name = parts[1] || '';
        group = '';
        baseUnit = parts[2] || '';
        grams = g4;
      } else {
        productCode = parts[0] || '';
        name = parts[1] || '';
        group = parts[2] || '';
        baseUnit = parts[3] || '';
      }
    } else if (parts.length === 3 && parseGramValue(parts[2]) !== undefined) {
      name = parts[0] || '';
      baseUnit = parts[1] || '';
      grams = parseGramValue(parts[2]);
      productCode = autoProductCodeFromName(name, usedCodes);
      group = '';
    } else {
      continue;
    }

    if (!name.trim()) continue;
    if (!productCode.trim()) {
      productCode = autoProductCodeFromName(name, usedCodes);
    } else {
      usedCodes.add(productCode.trim().toLowerCase());
    }

    out.push({
      productCode: productCode.trim(),
      name: name.trim(),
      group: group.trim(),
      baseUnit: (baseUnit || 'g').trim(),
      gramsPerBaseUnit: grams,
      lineNumber,
    });
  }

  return out;
};

export const parseGoodsCatalogExcel = (file: File): Promise<ParsedGoodsRow[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        if (!data) {
          resolve([]);
          return;
        }
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
        const out: ParsedGoodsRow[] = [];
        let lineNumber = 0;
        const usedCodes = new Set<string>();

        for (const row of json) {
          lineNumber += 1;
          const productCode = pick(row, [
            'Mã sản phẩm',
            'Ma san pham',
            'Mã SP',
            'Mã hàng',
            'productCode',
            'code',
          ]);
          const name = pick(row, [
            'Tên nguyên liệu',
            'Ten nguyen lieu',
            'Tên hàng',
            'Tên hàng hóa',
            'Tên',
            'name',
            'ingredientName',
          ]);
          const group = pick(row, ['Nhóm', 'Nhom', 'Group', 'group']);
          const baseUnit = pick(row, [
            'Đơn vị gốc',
            'Don vi goc',
            'Đơn vị',
            'Đơn vị tính',
            'unit',
            'baseUnit',
          ]);
          let grams = pickGram(row);

          let pc = productCode.trim();
          let nm = name.trim();
          let gr = group.trim();
          let bu = (baseUnit || 'g').trim();

          if (nm && !pc) {
            pc = autoProductCodeFromName(nm, usedCodes);
          } else if (pc) {
            usedCodes.add(pc.toLowerCase());
          }

          if (!nm) continue;
          out.push({
            productCode: pc,
            name: nm,
            group: gr,
            baseUnit: bu,
            gramsPerBaseUnit: grams,
            lineNumber,
          });
        }
        resolve(out);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
};

export const parsedRowsToDraftItems = (
  rows: ParsedGoodsRow[],
): Omit<WarehouseGoodsItem, 'id' | 'createdAt' | 'updatedAt'>[] => {
  return rows
    .map((r, idx) => {
      const unit = (r.baseUnit || 'g').trim();
      const grams = r.gramsPerBaseUnit ?? inferDefaultGramsPerBaseUnit(unit);
      return {
        productCode: r.productCode.trim(),
        name: r.name.trim(),
        group: r.group.trim(),
        baseUnit: unit,
        gramsPerBaseUnit: grams,
        sortIndex: idx,
      };
    })
    .filter((r) => r.productCode && r.name && r.gramsPerBaseUnit > 0);
};

/**
 * Trùng tên trong file; trùng tên với bản ghi khác mã khi không replace all.
 */
export function validateWarehouseGoodsNamesUnique(
  items: Array<{ productCode: string; name: string }>,
  existing: WarehouseGoodsItem[],
  replaceAll: boolean,
): string[] {
  const errors: string[] = [];
  const nameKeyToCode = new Map<string, string>();

  for (const it of items) {
    const nk = normalizeGoodsNameKey(it.name);
    if (!nk) continue;
    const code = it.productCode.trim().toLowerCase();
    const prevCode = nameKeyToCode.get(nk);
    if (prevCode !== undefined && prevCode !== code) {
      errors.push(`Trùng tên «${it.name}» nhưng hai mã sản phẩm khác nhau trong cùng file nhập.`);
      continue;
    }
    nameKeyToCode.set(nk, code);
  }

  if (!replaceAll) {
    const sanitizeId = (code: string) =>
      code.replace(/[^\w\u00C0-\u024f-]+/gi, '_').replace(/^_+|_+$/g, '') || '';

    for (const it of items) {
      const nk = normalizeGoodsNameKey(it.name);
      if (!nk) continue;
      const codeKey = it.productCode.trim().toLowerCase();
      const prev = existing.find((e) => (e.productCode || '').trim().toLowerCase() === codeKey);
      const targetId = prev?.id || sanitizeId(it.productCode);

      const conflict = existing.find(
        (e) => normalizeGoodsNameKey(e.name) === nk && e.id !== targetId,
      );
      if (conflict) {
        errors.push(
          `Tên «${it.name}» đã có trong danh mục (mã «${conflict.productCode}»), không khớp mã «${it.productCode}».`,
        );
      }
    }
  }

  return Array.from(new Set(errors));
}
